import { useState, useEffect } from 'react';
import { FilterPanel, FilterSection, FilterCheckbox, FilterEmptyNote } from '../../components/FilterPanel';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FilterState {
  roles: Set<string>;
  locationIds: Set<string>;
}

export interface LocationOption {
  id: string;
  name: string;
}

interface EmployeeFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  currentFilters: FilterState;
  availableLocations: LocationOption[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Site Manager' },
  { value: 'MEMBER', label: 'Employee' },
  { value: 'GUEST', label: 'Guest' },
] as const;

const toggleInSet = (set: Set<string>, value: string) => {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};

// ─── Component ────────────────────────────────────────────────────────────────

export function EmployeeFilterPanel({
  isOpen,
  onClose,
  onApply,
  currentFilters,
  availableLocations,
}: EmployeeFilterPanelProps) {
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set(currentFilters.roles));
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(
    new Set(currentFilters.locationIds),
  );
  const [roleExpanded, setRoleExpanded] = useState(true);
  const [locationExpanded, setLocationExpanded] = useState(true);

  // Sync local state when panel opens or currentFilters change
  useEffect(() => {
    if (isOpen) {
      setSelectedRoles(new Set(currentFilters.roles));
      setSelectedLocationIds(new Set(currentFilters.locationIds));
    }
  }, [isOpen, currentFilters]);

  const chips = [
    ...ROLE_OPTIONS.filter(opt => selectedRoles.has(opt.value)).map(opt => ({
      key: `role-${opt.value}`,
      label: opt.label,
      onRemove: () => setSelectedRoles(prev => toggleInSet(prev, opt.value)),
    })),
    ...availableLocations.filter(loc => selectedLocationIds.has(loc.id)).map(loc => ({
      key: `location-${loc.id}`,
      label: loc.name,
      onRemove: () => setSelectedLocationIds(prev => toggleInSet(prev, loc.id)),
    })),
  ];

  return (
    <FilterPanel
      isOpen={isOpen}
      ariaLabel="Filter employees"
      chips={chips}
      onClose={onClose}
      onApply={() => {
        onApply({ roles: new Set(selectedRoles), locationIds: new Set(selectedLocationIds) });
        onClose();
      }}
      onClearAll={() => {
        setSelectedRoles(new Set());
        setSelectedLocationIds(new Set());
      }}
    >
      <FilterSection
        title="Role"
        count={selectedRoles.size}
        expanded={roleExpanded}
        onToggle={() => setRoleExpanded(p => !p)}
      >
        {ROLE_OPTIONS.map(opt => (
          <FilterCheckbox
            key={opt.value}
            label={opt.label}
            checked={selectedRoles.has(opt.value)}
            onChange={() => setSelectedRoles(prev => toggleInSet(prev, opt.value))}
          />
        ))}
      </FilterSection>

      <FilterSection
        title="Location"
        count={selectedLocationIds.size}
        expanded={locationExpanded}
        onToggle={() => setLocationExpanded(p => !p)}
      >
        {availableLocations.length === 0 ? (
          <FilterEmptyNote>No locations available</FilterEmptyNote>
        ) : (
          availableLocations.map(loc => (
            <FilterCheckbox
              key={loc.id}
              label={loc.name}
              checked={selectedLocationIds.has(loc.id)}
              onChange={() => setSelectedLocationIds(prev => toggleInSet(prev, loc.id))}
            />
          ))
        )}
      </FilterSection>
    </FilterPanel>
  );
}
